/**
 * IntegrationsPanel — mailbox connection surfaces (Outlook, Gmail).
 *
 * The Outlook card is always visible. The Google card only renders when
 * the client-side VITE_FEATURE_GMAIL_OAUTH flag is set, mirroring the
 * server-side FEATURE_GMAIL_OAUTH gate. Hiding the button when the flag
 * is off avoids reps clicking into a 503 dead end.
 *
 * The page-level /settings/integrations route (pages/settings/Integrations.tsx)
 * already shows CRM cards (Salesforce/HubSpot/Dynamics). This panel adds
 * mailbox cards underneath that grid and is the home for everything
 * Tier 1 Activity Capture exposes to the rep.
 *
 * Author: Sub-D (Tier 1 Activity Capture, phase 3)
 */

import { useCallback, useEffect, useState } from "react";
import { tokens } from "@/components/operator/tokens";
import { isGmailOAuthEnabledClient } from "../../../../shared/feature-flags";

type MailboxProvider = "microsoft" | "google";
type ConnStatus = "disconnected" | "connected" | "connecting" | "error";

interface IntegrationRow {
  provider: MailboxProvider;
  email: string | null;
  connected_at: string | null;
  revoked_at: string | null;
}

interface CardProps {
  provider: MailboxProvider;
  label: string;
  blurb: string;
  status: ConnStatus;
  connectedEmail: string | null;
  errorMessage: string | null;
  onConnect: () => void;
  onDisconnect: () => void;
}

function ProviderLogo({ provider }: { provider: MailboxProvider }) {
  const isMs = provider === "microsoft";
  return (
    <div
      className="flex items-center justify-center rounded-lg flex-shrink-0 font-black"
      style={{
        background: "#FFFFFF",
        border: `2px solid ${tokens.color.black}`,
        color: tokens.color.black,
        width: 48,
        height: 48,
        fontSize: 13,
        letterSpacing: "0.02em",
      }}
      aria-label={isMs ? "Outlook" : "Gmail"}
    >
      {isMs ? "MS" : "GM"}
    </div>
  );
}

function StatusPill({ status }: { status: ConnStatus }) {
  const map: Record<
    ConnStatus,
    { bg: string; fg: string; dot: string; label: string }
  > = {
    connected: {
      bg: tokens.color.successBg,
      fg: "#166534",
      dot: tokens.color.success,
      label: "Connected",
    },
    connecting: {
      bg: tokens.color.amberBg,
      fg: "#92400E",
      dot: tokens.color.amber,
      label: "Connecting",
    },
    error: {
      bg: tokens.color.dangerBg,
      fg: "#991B1B",
      dot: tokens.color.danger,
      label: "Error",
    },
    disconnected: {
      bg: tokens.color.infoBg,
      fg: tokens.color.textMuted,
      dot: tokens.color.textSubtle,
      label: "Disconnected",
    },
  };
  const s = map[status];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full uppercase font-semibold"
      style={{
        background: s.bg,
        color: s.fg,
        fontSize: 11,
        padding: "4px 10px",
        letterSpacing: "0.06em",
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: s.dot }}
      />
      {s.label}
    </span>
  );
}

function MailboxCard({
  provider,
  label,
  blurb,
  status,
  connectedEmail,
  errorMessage,
  onConnect,
  onDisconnect,
}: CardProps) {
  return (
    <div
      className="bg-white rounded-xl p-6 flex flex-col"
      style={{ border: `1px solid ${tokens.color.border}`, minHeight: 240 }}
    >
      <div className="flex items-start gap-4 mb-4">
        <ProviderLogo provider={provider} />
        <div className="min-w-0 flex-1">
          <div
            className="font-semibold mb-0.5"
            style={{ color: tokens.color.textPrimary, fontSize: 16 }}
          >
            {label}
          </div>
          <div style={{ fontSize: 12, color: tokens.color.textMuted }}>
            {blurb}
          </div>
        </div>
      </div>

      <div className="mb-3">
        <StatusPill status={status} />
      </div>

      {connectedEmail && (
        <div
          style={{
            fontSize: 12,
            color: tokens.color.textMuted,
            marginBottom: 8,
          }}
        >
          Mailbox: <strong style={{ color: tokens.color.textPrimary }}>{connectedEmail}</strong>
        </div>
      )}

      {errorMessage && (
        <div
          style={{
            fontSize: 12,
            color: "#991B1B",
            background: tokens.color.dangerBg,
            padding: "6px 10px",
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          {errorMessage}
        </div>
      )}

      <div className="mt-auto flex gap-2">
        {status === "connected" ? (
          <button
            onClick={onDisconnect}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
            style={{
              background: "#FFFFFF",
              color: tokens.color.textPrimary,
              border: `1px solid ${tokens.color.border}`,
            }}
          >
            Disconnect
          </button>
        ) : (
          <button
            onClick={onConnect}
            disabled={status === "connecting"}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: tokens.color.black }}
          >
            {status === "connecting" ? "Redirecting…" : `Connect ${label}`}
          </button>
        )}
      </div>
    </div>
  );
}

export default function IntegrationsPanel() {
  const [rows, setRows] = useState<IntegrationRow[]>([]);
  const [statusByProvider, setStatusByProvider] = useState<
    Record<MailboxProvider, ConnStatus>
  >({
    microsoft: "disconnected",
    google: "disconnected",
  });
  const [errorByProvider, setErrorByProvider] = useState<
    Record<MailboxProvider, string | null>
  >({
    microsoft: null,
    google: null,
  });

  const gmailEnabled = isGmailOAuthEnabledClient();

  // ── Surface ?connected / ?error query params from the OAuth callback ─
  useEffect(() => {
    const hash = window.location.hash;
    const qIdx = hash.indexOf("?");
    if (qIdx < 0) return;
    const q = new URLSearchParams(hash.slice(qIdx + 1));
    const connected = q.get("connected");
    const error = q.get("error");
    if (connected === "microsoft" || connected === "google") {
      setStatusByProvider((s) => ({ ...s, [connected]: "connected" }));
    }
    if (error) {
      // Best-effort attribution to the provider whose start was clicked
      // most recently — stored on sessionStorage in onConnect below.
      const last =
        (sessionStorage.getItem("aip:lastOauthProvider") as MailboxProvider | null) ??
        "microsoft";
      setErrorByProvider((s) => ({ ...s, [last]: error }));
      setStatusByProvider((s) => ({ ...s, [last]: "error" }));
    }
  }, []);

  // ── Fetch current connections ────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await fetch("/api/me/integrations", {
          credentials: "include",
        });
        if (!resp.ok) return;
        const body = (await resp.json()) as { connections?: IntegrationRow[] };
        if (cancelled) return;
        const list = body.connections ?? [];
        setRows(list);
        setStatusByProvider((s) => {
          const next = { ...s };
          for (const r of list) {
            if (r.revoked_at == null) next[r.provider] = "connected";
          }
          return next;
        });
      } catch {
        // Endpoint may not be wired yet — keep cards in disconnected state.
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const onConnect = useCallback((provider: MailboxProvider) => {
    setStatusByProvider((s) => ({ ...s, [provider]: "connecting" }));
    setErrorByProvider((s) => ({ ...s, [provider]: null }));
    sessionStorage.setItem("aip:lastOauthProvider", provider);
    void (async () => {
      try {
        const resp = await fetch(`/api/oauth/${provider}/start`, {
          credentials: "include",
        });
        if (!resp.ok) {
          const body = (await resp.json().catch(() => ({}))) as {
            error?: string;
          };
          const msg =
            body.error ??
            (resp.status === 503
              ? "Integration not enabled"
              : "Failed to start OAuth");
          setStatusByProvider((s) => ({ ...s, [provider]: "error" }));
          setErrorByProvider((s) => ({ ...s, [provider]: msg }));
          return;
        }
        const { redirectUrl } = (await resp.json()) as { redirectUrl: string };
        window.location.assign(redirectUrl);
      } catch (err) {
        setStatusByProvider((s) => ({ ...s, [provider]: "error" }));
        setErrorByProvider((s) => ({
          ...s,
          [provider]: "Network error reaching OAuth start",
        }));
        console.error("[IntegrationsPanel] start failed", err);
      }
    })();
  }, []);

  const onDisconnect = useCallback((provider: MailboxProvider) => {
    void (async () => {
      try {
        const resp = await fetch(`/api/oauth/${provider}/revoke`, {
          method: "POST",
          credentials: "include",
        });
        if (!resp.ok) {
          const body = (await resp.json().catch(() => ({}))) as {
            error?: string;
          };
          setErrorByProvider((s) => ({
            ...s,
            [provider]: body.error ?? "Failed to revoke",
          }));
          return;
        }
        setStatusByProvider((s) => ({ ...s, [provider]: "disconnected" }));
        setErrorByProvider((s) => ({ ...s, [provider]: null }));
        setRows((rs) => rs.filter((r) => r.provider !== provider));
      } catch (err) {
        setErrorByProvider((s) => ({
          ...s,
          [provider]: "Network error reaching revoke",
        }));
        console.error("[IntegrationsPanel] revoke failed", err);
      }
    })();
  }, []);

  const microsoftEmail =
    rows.find((r) => r.provider === "microsoft" && r.revoked_at == null)?.email ??
    null;
  const googleEmail =
    rows.find((r) => r.provider === "google" && r.revoked_at == null)?.email ??
    null;

  return (
    <div
      className="rounded-xl bg-white p-6 space-y-5"
      style={{ border: `1px solid ${tokens.color.border}` }}
    >
      <div>
        <div
          className="text-xs font-bold uppercase tracking-[0.2em]"
          style={{ color: tokens.color.textPrimary }}
        >
          Mailbox capture
        </div>
        <p
          style={{
            fontSize: 13,
            color: tokens.color.textMuted,
            marginTop: 8,
            lineHeight: 1.5,
            maxWidth: 720,
          }}
        >
          Connect your work mailbox so Attilius can verify the activity you
          report. Sent emails and meetings flow into the system-of-record
          column on your end-of-day reconciliation.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <MailboxCard
          provider="microsoft"
          label="Outlook"
          blurb="Microsoft 365 mail and calendar. Read-only by default."
          status={statusByProvider.microsoft}
          connectedEmail={microsoftEmail}
          errorMessage={errorByProvider.microsoft}
          onConnect={() => onConnect("microsoft")}
          onDisconnect={() => onDisconnect("microsoft")}
        />

        {gmailEnabled ? (
          <MailboxCard
            provider="google"
            label="Gmail"
            blurb="Google Workspace mail and calendar. Read-only by default."
            status={statusByProvider.google}
            connectedEmail={googleEmail}
            errorMessage={errorByProvider.google}
            onConnect={() => onConnect("google")}
            onDisconnect={() => onDisconnect("google")}
          />
        ) : (
          <div
            className="rounded-xl p-6 flex flex-col justify-center"
            style={{
              border: `1px dashed ${tokens.color.border}`,
              background: tokens.color.cardBg,
              minHeight: 240,
            }}
          >
            <div
              className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2"
              style={{ color: tokens.color.textSubtle }}
            >
              Coming soon
            </div>
            <div
              className="font-semibold"
              style={{ color: tokens.color.textPrimary, fontSize: 15 }}
            >
              Gmail and Google Calendar
            </div>
            <p
              style={{
                fontSize: 12,
                color: tokens.color.textMuted,
                marginTop: 6,
                lineHeight: 1.5,
              }}
            >
              Google Workspace mailboxes will be supported in a follow-up
              release. Outlook reps can connect now.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
